import { createSpecificSkillCertificateApi, useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataCreateSpecificSkillCertificateSchema } from "@/features/master-data/schema/masterDataSpecificSkillCertificateSchema";
import { TMasterSpecificSkillCertificateCreateForm } from "@/features/master-data/types/masterDataSpecificSkillCertificateForm";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { ESpecificSkillCertificateType, SPECIFIC_SKILL_CERTIFICATE_TYPE } from "@/types/enum";
import { objectToOption } from "@/utils/object";

type TCreateSpecificSkillCertificateFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

export const CreateSpecificSkillCertificateFormModal: FC<TCreateSpecificSkillCertificateFormModalProps> = ({ onClose, isOpen }) => {
  const queryClient = useQueryClient();

  const methods = useForm<TMasterSpecificSkillCertificateCreateForm>({
    resolver: yupResolver(masterDataCreateSpecificSkillCertificateSchema),
    mode: "onBlur",
    defaultValues: {
      certificateName: "",
      certificateType: ESpecificSkillCertificateType.SPECIFIED_SKILL_NO1,
      description: "",
      companyIndustryIds: [],
    },
  });

  const { isLoading: isLoadingCompanySpecificIndustryList, companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();

  const { control } = methods;

  const onSubmit = async (data: TMasterSpecificSkillCertificateCreateForm) => {
    try {
      const rs = await createSpecificSkillCertificateApi(data);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_SPECIFIC_SKILL_CERTIFICATE] });
      methods.reset();
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={600}
      title="追加"
      isOpen={isOpen}
      enableFooter={false}
      onClose={() => {
        methods.reset();
        onClose();
      }}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataCreateSpecificSkillCertificateSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={12}>
            <FormField control={control} name="certificateName" label="特定技能合格証">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="companyIndustryIds" label="分野">
              <SelectMulti
                options={companySpecificIndustryOptions}
                placeholder="選択してください"
                loading={isLoadingCompanySpecificIndustryList}
                separator="、"
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="certificateType" label="1号or2号">
              <Select options={objectToOption(SPECIFIC_SKILL_CERTIFICATE_TYPE)} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="description" label="説明">
              <Textarea minRows={2} maxRows={3} />
            </FormField>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
